import React, { type ReactNode, useEffect, useState } from './react';

export type RouteObject = {
  path?: string;
  index?: boolean;
  element: ReactNode;
  children?: RouteObject[];
};

type Router = { routes: RouteObject[] };
let activeOutlet: ReactNode = null;
let forceNavigationRender: (() => void) | null = null;

declare global { interface Window { __NEXO_ROUTE__?: string; __NEXO_SEARCH__?: string; } }
function currentPathname() { return window.__NEXO_ROUTE__ ?? window.location.pathname; }
export function currentSearch() { return window.__NEXO_SEARCH__ ?? window.location.search; }

export function navigate(to: string, options: { replace?: boolean } = {}) {
  const queryIndex = to.indexOf('?');
  const pathname = queryIndex >= 0 ? to.slice(0, queryIndex) : to;
  const search = queryIndex >= 0 ? to.slice(queryIndex) : '';
  if (window.__NEXO_ROUTE__) {
    window.__NEXO_ROUTE__ = pathname;
    window.__NEXO_SEARCH__ = search;
  } else if (options.replace) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  forceNavigationRender?.();
  window.scrollTo({ top: 0 });
}

export function createBrowserRouter(routes: RouteObject[]): Router { return { routes }; }

function matchesSegment(pattern: string, actual: string): boolean {
  return pattern.startsWith(':') || pattern === actual;
}

function resolve(routes: RouteObject[], pathname: string): ReactNode {
  const segments = pathname.split('/').filter(Boolean);
  for (const route of routes) {
    if (route.path === '*') continue;
    if (route.path === '/') {
      if (pathname === '/') return route.element;
      continue;
    }
    const baseSegments = (route.path ?? '').split('/').filter(Boolean);
    if (!baseSegments.every((segment, index) => matchesSegment(segment, segments[index] ?? ''))) continue;
    if (!route.children) return route.element;
    const rest = segments.slice(baseSegments.length);
    const child = rest.length === 0
      ? route.children.find((item) => item.index)
      : route.children.find((item) => {
          const childSegments = (item.path ?? '').split('/').filter(Boolean);
          return childSegments.length === rest.length && childSegments.every((segment, index) => matchesSegment(segment, rest[index]));
        });
    if (child) {
      activeOutlet = child.element;
      return route.element;
    }
  }
  return routes.find((route) => route.path === '*')?.element ?? null;
}

export function RouterProvider({ router }: { router: Router }) {
  const [, setVersion] = useState(0);
  forceNavigationRender = () => setVersion((value) => value + 1);
  useEffect(() => {
    const listener = () => setVersion((value) => value + 1);
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, []);
  activeOutlet = null;
  return <>{resolve(router.routes, currentPathname())}</>;
}

export function Outlet() { return <>{activeOutlet}</>; }

export function useLocation() { return { pathname: currentPathname(), search: currentSearch() }; }

export function useNavigate() { return navigate; }

export function useParams() {
  const segments = currentPathname().split('/').filter(Boolean);
  if (segments[0] === 'catalogo') return { slug: segments[1] };
  if (segments[0] === 'check-in') return { token: segments[1] };
  return {};
}

export function Link({ to, children, target, className, onClick: userOnClick, ...props }: Record<string, unknown> & { to: string; children?: ReactNode; target?: string; className?: string; onClick?: (event: MouseEvent) => void }) {
  const onClick = (event: MouseEvent) => {
    userOnClick?.(event);
    if (event.defaultPrevented || target === '_blank' || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    navigate(to);
  };
  return <a href={to} target={target} className={className} {...props} onClick={onClick}>{children}</a>;
}

export function NavLink({ to, children, end = false, className, ...props }: Record<string, unknown> & { to: string; children?: ReactNode; end?: boolean; className?: string | ((state: { isActive: boolean }) => string) }) {
  const pathname = currentPathname();
  const isActive = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  const resolvedClass = typeof className === 'function' ? className({ isActive }) : className;
  return <Link to={to} className={resolvedClass} {...props}>{children}</Link>;
}
